import { supabase } from "@/lib/supabase";
import type { Domain } from "@/types/domain";
import {
  dashboardAggregatesResponseSchema,
  type DashboardData,
  type DashboardQueryFilters,
  toGetDashboardAggregatesArgs,
} from "@/types/dashboard";
import type { DistributionItem } from "@/lib/utils";
import { getDateRangeDates } from "@/lib/compare-date-utils";
import { fetchUserProfile } from "@/hooks/useUser";
import { queryClient } from "@/lib/queryClient";
import { hasGlobalDashboardScope } from "@/lib/userRole";
import type { ProfileTeamScope } from "@/types/users";

/** Fluent subset used between `.select()` and `.range()` on `dashboard_domains`. */
type DomainsTableFilterableQuery = {
  in(column: string, values: string[]): DomainsTableFilterableQuery;
  overlaps(column: string, values: string[]): DomainsTableFilterableQuery;
  eq(column: string, value: string): DomainsTableFilterableQuery;
  gte(column: string, value: string): DomainsTableFilterableQuery;
  lte(column: string, value: string): DomainsTableFilterableQuery;
};

const DASHBOARD_LIMIT_OPTIONS = [15, 30, 50, 100] as const;

export type DashboardLimit = (typeof DASHBOARD_LIMIT_OPTIONS)[number];

export const DEFAULT_DASHBOARD_LIMIT: DashboardLimit = 15;

class DashboardService {
  private static instance: DashboardService;

  private constructor() {}

  public static getInstance(): DashboardService {
    if (!DashboardService.instance) {
      DashboardService.instance = new DashboardService();
    }
    return DashboardService.instance;
  }

  public getAllowedLimits() {
    return DASHBOARD_LIMIT_OPTIONS;
  }

  public normalizeLimit(limit: number): DashboardLimit {
    return (DASHBOARD_LIMIT_OPTIONS.includes(limit as DashboardLimit)
      ? limit
      : DEFAULT_DASHBOARD_LIMIT) as DashboardLimit;
  }

  /** Role admin or team `admins` → global visibility (no team filter). */
  private teamScopeForProfile(profile: ProfileTeamScope): string | undefined {
    if (hasGlobalDashboardScope(profile)) return undefined;
    return profile.team ?? undefined;
  }

  private applyFiltersToQuery<T extends DomainsTableFilterableQuery>(
    query: T,
    filters: DashboardQueryFilters,
    team?: string,
  ): T {
    let filteredQuery: DomainsTableFilterableQuery = query;
    const { geo, siteTypes, status, languages, keywords, from, to } = filters;

    if (team) filteredQuery = filteredQuery.eq("team", team);

    if (geo.length > 0) {
      filteredQuery = filteredQuery.overlaps("jetsik_countries", geo);
    }
    if (siteTypes.length > 0) {
      filteredQuery = filteredQuery.in("site_type", siteTypes);
    }
    if (status.length > 0) filteredQuery = filteredQuery.in("status", status);
    if (languages.length > 0) {
      filteredQuery = filteredQuery.overlaps("jetsik_languages", languages);
    }
    if (keywords.length > 0) {
      filteredQuery = filteredQuery.in("jetsik_brand", keywords);
    }
    if (from) filteredQuery = filteredQuery.gte("created_at", from);
    if (to) filteredQuery = filteredQuery.lte("created_at", to);

    return filteredQuery as T;
  }

  private formatDistribution(
    data: Record<string, number> | undefined | null,
  ): DistributionItem[] {
    if (!data) return [];

    const sorted = Object.entries(data)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);

    const total = sorted.reduce((sum, item) => sum + item.count, 0);

    return sorted.map(({ label, count }) => ({
      label,
      count,
      percentage:
        total > 0 ? Number(((count / total) * 100).toFixed(1)) : 0,
    }));
  }

  /** GEO and Language dropdown values: the codes Jetsik actually uses. */
  /**
   * Top brands by Keitaro revenue over the last 30 days, for the Revenue card.
   * Not narrowed by the dashboard's domain filters: those filter Domain
   * Tracker rows, revenue comes from clicks_daily.
   */
  public async getRevenueByBrand(): Promise<DistributionItem[]> {
    const { current } = getDateRangeDates("last_30_days");
    const { data, error } = await supabase.rpc("get_dynamic_stats", {
      dim: "brand",
      p_start_date: current.start ?? undefined,
      p_end_date: current.end,
    });
    if (error) throw new Error(error.message);
    // ponytail: PostgREST returns the first 1000 rows, already ordered by
    // revenue; 920 brands had clicks in 30 days (30.09.2026). Past 1000 the
    // tail's small revenue drops out of the % base - paginate if that matters.
    const rows = (data ?? [])
      .map((r) => ({ label: r.dimension_value, revenue: Number(r.total_revenue) || 0 }))
      .filter((r) => r.revenue > 0);
    const total = rows.reduce((s, r) => s + r.revenue, 0);
    return rows
      .sort((a, b) => b.revenue - a.revenue)
      .map((r) => ({
        label: r.label === "Not set" ? "No brand" : r.label,
        count: Math.round(r.revenue),
        percentage: total > 0 ? Math.round((r.revenue / total) * 1000) / 10 : 0,
      }));
  }

  public async getJetsikFilterOptions(): Promise<{
    countries: string[];
    languages: string[];
  }> {
    const { data, error } = await supabase
      .from("jetsik_filter_options")
      .select("countries, languages")
      .single();

    if (error) throw error;

    return {
      countries: data.countries ?? [],
      languages: data.languages ?? [],
    };
  }

  public async getDashboardData(
    page: number,
    filters: DashboardQueryFilters,
    limit: number,
  ): Promise<DashboardData> {
    const { profile } = await queryClient.fetchQuery({
      queryKey: ["authUser"],
      queryFn: fetchUserProfile,
      staleTime: 1000 * 60 * 5,
    });

    if (!profile) throw new Error("Unauthorized");

    const teamScope = this.teamScopeForProfile(profile);

    const pageSize = this.normalizeLimit(limit);
    const fromIdx = (page - 1) * pageSize;
    const toIdx = fromIdx + pageSize - 1;

    // Intentional, not an oversight: Dashboard and Reports answer "what do we
    // own right now" and exclude disabled domains, while Clicks and GSC answer
    // "what happened in this period" and keep them, so history is not rewritten
    // when a domain is switched off. Domain counts differing between these
    // pages is expected. Reviewed 2026-08-25 — do not "unify" without a
    // product decision.
    //
    // Only domains Jetsik treats as live (indexation, indexed, redirected) are
    // listed; the rest stay in domains and are reachable in Supabase only.
    // Decided 2026-09-14. Known gap: live WordPress/HTML sites are not in Jetsik
    // and are hidden too — see migration 20260914120000_dashboard_from_jetsik.
    let tableQuery = supabase
      .from("dashboard_domains")
      .select("*", { count: "exact" })
      .eq('is_active', true)
      .order("created_at", { ascending: false })
      .order("id", { ascending: true });

    tableQuery = this.applyFiltersToQuery(tableQuery, filters, teamScope);

    const rpcQuery = supabase.rpc(
      "get_dashboard_aggregates",
      toGetDashboardAggregatesArgs(filters, teamScope),
    );

    const [tableResult, rpcResult] = await Promise.all([
      tableQuery.range(fromIdx, toIdx),
      rpcQuery,
    ]);

    const { data: tableData, count, error: tableError } = tableResult;
    const { data: aggregatedRaw, error: rpcError } = rpcResult;

    if (tableError) throw tableError;
    if (rpcError) throw rpcError;

    const aggregates = dashboardAggregatesResponseSchema.parse(aggregatedRaw);

    return {
      // GEO, Lang and Keyword (brand) show Jetsik's values. The Domain Tracker
      // ones stay in domains for Keyword Volume and the CSV import.
      domains: (tableData ?? []).map((row) => ({
        ...row,
        geo: row.jetsik_countries?.join(", ") ?? "",
        lang: row.jetsik_languages?.join(", ") ?? "",
        brand_keyword: row.jetsik_brand ?? "",
      })) as Domain[],
      totalCount: count ?? 0,
      page,
      stats: aggregates.stats,
      distributions: {
        siteTypes: this.formatDistribution(
          aggregates.distributions.siteTypes,
        ),
        geo: this.formatDistribution(aggregates.distributions.geo),
        keywords: this.formatDistribution(
          aggregates.distributions.keywords,
        ),
        languages: this.formatDistribution(
          aggregates.distributions.languages,
        ),
      },
    };
  }
}

export const dashboardService = DashboardService.getInstance();
